import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Response } from 'cross-fetch'
import debounce from 'lodash.debounce'

import { ProjectFields, YesObject } from '@yes.technology/react-toolkit'

import { useAppDispatch, useAppSelector } from 'state/store'

import { FlatInteractionModelData } from 'types/shared'
import api from 'utils/api'
import { FilteractionParams } from 'utils/apiTypes'

type AutocompleteResponse = {
  data:
    | YesObject[]
    | {
        objects: YesObject[]
      }
  message: string
  success: boolean
}

type UseFetchSuggestionsProps = {
  parentPath: string[]
  filteractionParams?: object[]
  filteraction?: string
  endpoint?: string
  showLoading?: boolean
  projectFields?: ProjectFields
}

type FetchSuggestionsArgsProps = UseFetchSuggestionsProps & {
  activityValues?: FlatInteractionModelData['activity_values']
}

export default function useFetchSuggestions({
  parentPath,
  filteractionParams,
  filteraction,
  endpoint,
  showLoading = false,
  projectFields
}: UseFetchSuggestionsProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<YesObject[]>([])

  const ignoreResponse = useRef(false)

  const fetchSuggestionsSyncedArgs = useRef<FetchSuggestionsArgsProps>({
    parentPath,
    filteractionParams,
    filteraction,
    endpoint,
    showLoading,
    activityValues: {}
  })

  const dispatch = useAppDispatch()

  const resetSuggestions = () => {
    debouncedFetchSuggestions.cancel()
    ignoreResponse.current = true

    setIsLoading(false)
    setSuggestions([])
  }

  const { interactionModel } = useAppSelector(
    ({ interactions }) => interactions
  )

  useEffect(() => {
    const activityValues = interactionModel?.flatData?.activity_values

    fetchSuggestionsSyncedArgs.current = {
      parentPath,
      filteractionParams,
      filteraction,
      endpoint,
      showLoading,
      activityValues: activityValues
    }
  }, [
    endpoint,
    showLoading,
    filteraction,
    filteractionParams,
    interactionModel?.flatData?.activity_values,
    parentPath
  ])

  const fetchSuggestions = useCallback(
    async (value: string) => {
      ignoreResponse.current = false

      const {
        parentPath,
        filteractionParams,
        filteraction,
        endpoint,
        showLoading,
        activityValues
      } = fetchSuggestionsSyncedArgs.current

      try {
        setIsLoading(true)

        const throwError = false

        const response: AutocompleteResponse = await api.getAutocomplete(
          value,
          endpoint || '',
          !!showLoading,
          filteraction,
          filteractionParams as FilteractionParams[],
          activityValues || {},
          parentPath,
          throwError,
          projectFields
        )

        if (response?.success === true && !ignoreResponse.current) {
          let results = response.data

          if ('objects' in response.data) {
            results = response.data.objects
          }

          if (!Array.isArray(results)) {
            results = []
          }

          setSuggestions(results)
        } else {
          if (!ignoreResponse.current) {
            throw new Error(response?.message || 'Response vazia ou com erro')
          }
        }
      } catch (error) {
        if (!ignoreResponse.current) {
          if (error instanceof Response) {
            dispatch({ type: 'api/ERROR', data: { status: error.status } })
          }

          console.error(error)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, projectFields]
  )

  const debouncedFetchSuggestions = useMemo(
    () => debounce(fetchSuggestions, 350),
    [fetchSuggestions]
  )

  return {
    suggestions,
    setSuggestions,
    isLoading,
    fetchSuggestions,
    debouncedFetchSuggestions,
    resetSuggestions
  }
}
